/**
 * Enums para facilitar o cadastro de Prestadores de Serviço Externo
 */

/**
 * Estados brasileiros (UF)
 */
export enum BrazilianState {
  AC = 'AC',
  AL = 'AL',
  AP = 'AP',
  AM = 'AM',
  BA = 'BA',
  CE = 'CE',
  DF = 'DF',
  ES = 'ES',
  GO = 'GO',
  MA = 'MA',
  MT = 'MT',
  MS = 'MS',
  MG = 'MG',
  PA = 'PA',
  PB = 'PB',
  PR = 'PR',
  PE = 'PE',
  PI = 'PI',
  RJ = 'RJ',
  RN = 'RN',
  RS = 'RS',
  RO = 'RO',
  RR = 'RR',
  SC = 'SC',
  SP = 'SP',
  SE = 'SE',
  TO = 'TO'
}

/**
 * Classificação do prestador (Tipo de Serviço) — cores da legenda operacional.
 */
export enum ServiceType {
  PRINCIPAL_CIDADE_FATURA = 'FORNECEDORES PRINCIPAL DA CIDADE ATIVO E QUE FATURA',
  ATIVOS_FATURAM = 'FORNECEDORES ATIVOS E QUE FATURAM',
  ATIVOS_AVISTA = 'FORNECEDORES ATIVOS COM PAGAMENTO Á VISTA',
  BLOQUEADOS = 'FORNECEDORES BLOQUEADOS OU QUE NÃO NOS ATENDE',
  GUINCHO = 'FORNECEDORES DE GUINCHO'
}

/** Cor de fundo associada a cada classificação (legenda). */
export const SERVICE_TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  [ServiceType.PRINCIPAL_CIDADE_FATURA]: { bg: '#5dade2', text: '#0b2e3f' },
  [ServiceType.ATIVOS_FATURAM]: { bg: '#28a745', text: '#ffffff' },
  [ServiceType.ATIVOS_AVISTA]: { bg: '#ffc107', text: '#212529' },
  [ServiceType.BLOQUEADOS]: { bg: '#dc3545', text: '#ffffff' },
  [ServiceType.GUINCHO]: { bg: '#e83e8c', text: '#ffffff' }
};

export function getServiceTypeColor(serviceType: string | null | undefined): { bg: string; text: string } | null {
  if (!serviceType) return null;
  return SERVICE_TYPE_COLORS[serviceType] ?? null;
}

export function getServiceTypeCssClass(serviceType: string | null | undefined): string {
  if (!serviceType) return '';
  const map: Record<string, string> = {
    [ServiceType.PRINCIPAL_CIDADE_FATURA]: 'svc-type--cyan',
    [ServiceType.ATIVOS_FATURAM]: 'svc-type--green',
    [ServiceType.ATIVOS_AVISTA]: 'svc-type--yellow',
    [ServiceType.BLOQUEADOS]: 'svc-type--red',
    [ServiceType.GUINCHO]: 'svc-type--pink'
  };
  return map[serviceType] ?? '';
}

/**
 * Tipos de Veículos atendidos
 */
export enum VehicleType {
  CARRO_PASSEIO = 'Carros de Passeio',
  MOTOCICLETA = 'Motocicletas',
  UTILITARIO = 'Utilitários',
  CAMINHAO_LEVE = 'Caminhões Leves',
  CAMINHAO_PESADO = 'Caminhões Pesados',
  ONIBUS = 'Ônibus',
  VAN = 'Vans',
  PICKUP = 'Pick-ups',
  SUV = 'SUVs',
  TODOS = 'Todos os Tipos'
}

/**
 * Tipos de Documentos
 */
export enum DocumentType {
  ALVARA = 'Alvará',
  CERTIFICADO = 'Certificado',
  FISCAL = 'Fiscal',
  SEGURO = 'Seguro',
  LICENCA = 'Licença',
  REGISTRO = 'Registro',
  CONTRATO = 'Contrato',
  OUTROS = 'Outros'
}

/**
 * Nomes padrão de documentos
 */
export enum DocumentName {
  ALVARA_FUNCIONAMENTO = 'Alvará de Funcionamento',
  CERTIFICADO_MEI = 'Certificado MEI',
  COMPROVANTE_REGULARIDADE_FISCAL = 'Comprovante de Regularidade Fiscal',
  SEGURO_RESPONSABILIDADE_CIVIL = 'Seguro de Responsabilidade Civil',
  LICENCA_AMBIENTAL = 'Licença Ambiental',
  REGISTRO_CONSELHO_REGIONAL = 'Registro no Conselho Regional'
}

/**
 * Helper para obter array de estados brasileiros
 */
export function getBrazilianStates(): { value: string; label: string }[] {
  return Object.entries(BrazilianState).map(([key, value]) => ({
    value,
    label: `${value} - ${getStateName(value)}`
  }));
}

/**
 * Helper para obter nome completo do estado
 */
function getStateName(uf: string): string {
  const stateNames: { [key: string]: string } = {
    AC: 'Acre',
    AL: 'Alagoas',
    AP: 'Amapá',
    AM: 'Amazonas',
    BA: 'Bahia',
    CE: 'Ceará',
    DF: 'Distrito Federal',
    ES: 'Espírito Santo',
    GO: 'Goiás',
    MA: 'Maranhão',
    MT: 'Mato Grosso',
    MS: 'Mato Grosso do Sul',
    MG: 'Minas Gerais',
    PA: 'Pará',
    PB: 'Paraíba',
    PR: 'Paraná',
    PE: 'Pernambuco',
    PI: 'Piauí',
    RJ: 'Rio de Janeiro',
    RN: 'Rio Grande do Norte',
    RS: 'Rio Grande do Sul',
    RO: 'Rondônia',
    RR: 'Roraima',
    SC: 'Santa Catarina',
    SP: 'São Paulo',
    SE: 'Sergipe',
    TO: 'Tocantins'
  };
  return stateNames[uf] || uf;
}

/**
 * Helper para obter array de tipos de serviço (classificação com cor).
 */
export function getServiceTypes(): { value: string; label: string; bg: string; text: string }[] {
  return Object.values(ServiceType).map(value => {
    const color = SERVICE_TYPE_COLORS[value] ?? { bg: '#e9ecef', text: '#212529' };
    return {
      value,
      label: value,
      bg: color.bg,
      text: color.text
    };
  });
}

/**
 * Helper para obter array de tipos de veículos
 */
export function getVehicleTypes(): { value: string; label: string }[] {
  return Object.values(VehicleType).map(value => ({
    value,
    label: value
  }));
}

/**
 * Helper para obter array de tipos de documentos
 */
export function getDocumentTypes(): { value: string; label: string }[] {
  return Object.values(DocumentType).map(value => ({
    value,
    label: value
  }));
}

